import { ConfigurationStatus } from "@/api/dtos";
import SourceDataEvents from "@/components/forms/forms/source-data/SourceDataEvents";
import ExcludedAlert from "@/components/layouts/ExcludedAlert";
import ConfigurationSourceDataContext from "@/contexts/data/ConfigurationSourceDataContext";
import CoordinatingGroupContext from "@/contexts/data/CoordinatingGroupContext";
import InstitutionContext from "@/contexts/data/InstitutionContext";
import MasterGroupContext from "@/contexts/data/MasterGroupContext";
import MetadataContext from "@/contexts/data/MetadataContext";
import ParticipantContext from "@/contexts/data/ParticipantContext";
import SourceDataContext from "@/contexts/data/SourceDataContext";
import TrialConfigurationSourceDataContext from "@/contexts/data/TrialConfigurationSourceDataContext";
import TrialContext from "@/contexts/data/TrialContext";
import { Alert, AlertTitle, Box } from "@mui/material";
import { FC, useContext } from "react";

const ParticipantSourceDataEvents: FC = () => {
    const { data: trial } = useContext(TrialContext);
    const { data: masterGroup } = useContext(MasterGroupContext);
    const { data: coordinatingGroup } = useContext(CoordinatingGroupContext);
    const { data: institution } = useContext(InstitutionContext);
    const { data: participant } = useContext(ParticipantContext);
    const { data: metadata } = useContext(MetadataContext);
    const { data: trialConfiguration } = useContext(TrialConfigurationSourceDataContext);
    const { data: configuration } = useContext(ConfigurationSourceDataContext);
    const { data: sourceData } = useContext(SourceDataContext);

    if (!trial) {
        return (
            <Alert
                severity="error"
            >
                <AlertTitle>No Trial</AlertTitle>
                No trial was found.
            </Alert>
        )
    }

    if (!participant) {
        return (
            <Alert
                severity="error"
            >
                <AlertTitle>No Participant</AlertTitle>
                No participant was found.
            </Alert>
        )
    }

    if (!metadata) {
        return (
            <Alert
                severity="error"
            >
                <AlertTitle>Trial Metadata Could Not Be Retrieved</AlertTitle>
                The metadata for the {trial.name} trial could not be retrieved from OpenClinica.
            </Alert>
        )
    }

    if (!trialConfiguration) {
        return (
            <Alert
                severity="error"
            >
                <AlertTitle>Trial Configuration Could Not Be Retrieved</AlertTitle>
                The global configuration for the {trial.name} trial could not be retrieved.
            </Alert>
        )
    }

    if (!configuration) {
        return (
            <Alert
                severity="error"
            >
                <AlertTitle>Allocated Configuration Could Not Be Retrieved</AlertTitle>
                The allocated configuration for participant {participant.studyNumber} on the {trial.name} trial could not be retrieved.
            </Alert>
        )
    }

    if (!sourceData) {
        return (
            <Alert
                severity="error"
            >
                <AlertTitle>Source Data Could Not Be Retrieved</AlertTitle>
                The source data for participant {participant.studyNumber} on the {trial.name} trial could not be retrieved.
            </Alert>
        )
    }

    return (
        <Box sx={{mt: 1}}>
            <ExcludedAlert
                participant={participant}
            >
                <SourceDataEvents 
                    trial={trial}
                    masterGroup={masterGroup}
                    coordinatingGroup={coordinatingGroup}
                    institution={institution}
                    participant={participant}
                    metadata={metadata}
                    trialConfiguration={trialConfiguration}
                    configuration={configuration}
                    sourceData={sourceData}
                />
            </ExcludedAlert>
        </Box>
    )
};

export default ParticipantSourceDataEvents;